import useSWR from 'swr';
import { fetcher } from '../lib/api';
import type {
  OutlookPerfSummaryResponse,
  OutlookPerfTimeseriesResponse,
  OutlookPerfFetchBreakdownResponse,
  OutlookPerfTopSlowResponse,
  OutlookPerfPerRequestResponse,
  OutlookPerfBySentenceResponse,
  OutlookPerfByClassifierResponse,
  OutlookPerfHedgeResponse,
  OutlookPerfRequestDetail,
  OutlookPerfLookupResponse,
} from '../types';

// Polling cadence for the Outlook perf page. These queries are heavier than
// the 5s live-stats endpoints (multi-collection aggregations), so we poll at
// 30s — fast enough to feel live, slow enough to not hammer the reflect DB.
const POLL_INTERVAL = 30000;

export interface OutlookPerfParams {
  from: string; // ISO
  to: string;   // ISO
  orgId?: string;
  userId?: string;
}

function buildQuery(p: OutlookPerfParams & { granularity?: string; limit?: number }) {
  const qs = new URLSearchParams();
  qs.set('from', p.from);
  qs.set('to', p.to);
  if (p.orgId) qs.set('orgId', p.orgId);
  if (p.userId) qs.set('userId', p.userId);
  if (p.granularity) qs.set('granularity', p.granularity);
  if (p.limit) qs.set('limit', String(p.limit));
  return qs.toString();
}

export function useOutlookPerfSummary(params: OutlookPerfParams | null) {
  const url = params ? `/api/stats/outlook-perf/summary?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<OutlookPerfSummaryResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function useOutlookPerfTimeseries(
  params: (OutlookPerfParams & { granularity?: 'auto' | 'hour' | 'day' }) | null,
) {
  const url = params ? `/api/stats/outlook-perf/timeseries?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<OutlookPerfTimeseriesResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function useOutlookPerfFetchBreakdown(params: OutlookPerfParams | null) {
  const url = params ? `/api/stats/outlook-perf/fetch-breakdown?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<OutlookPerfFetchBreakdownResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function useOutlookPerfTopSlow(params: (OutlookPerfParams & { limit?: number }) | null) {
  const url = params ? `/api/stats/outlook-perf/top-slow?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<OutlookPerfTopSlowResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function useOutlookPerfPerRequest(params: OutlookPerfParams | null) {
  const url = params ? `/api/stats/outlook-perf/per-request?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<OutlookPerfPerRequestResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function useOutlookPerfBySentence(params: OutlookPerfParams | null) {
  const url = params ? `/api/stats/outlook-perf/by-sentence-count?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<OutlookPerfBySentenceResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function useOutlookPerfByClassifier(params: OutlookPerfParams | null) {
  const url = params ? `/api/stats/outlook-perf/by-classifier?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<OutlookPerfByClassifierResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function useOutlookPerfHedge(params: OutlookPerfParams | null) {
  const url = params ? `/api/stats/outlook-perf/hedge?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<OutlookPerfHedgeResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

// Resolve the filter strings (id / domain / email) into display info so the
// page can show "Filtering by: Acme Inc · acme.com → 507f…" next to the
// inputs. Slower polling — only refreshes if the user changes the inputs.
export function useOutlookPerfLookup(org: string | undefined, user: string | undefined) {
  const qs = new URLSearchParams();
  if (org) qs.set('org', org);
  if (user) qs.set('user', user);
  const url = qs.toString() ? `/api/stats/outlook-perf/lookup?${qs.toString()}` : null;
  const { data, error, isLoading } = useSWR<OutlookPerfLookupResponse>(url, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 5000,
  });
  return { data: data ?? null, error, isLoading };
}

// Detail loader fetched on-demand when the user clicks a row in the top-slow
// table. Pass `null` to keep idle; pass an id to load. Manual refetch only —
// no polling — because the data is immutable per id.
export function useOutlookPerfRequestDetail(apiRequestId: string | null) {
  const url = apiRequestId ? `/api/stats/outlook-perf/request/${encodeURIComponent(apiRequestId)}` : null;
  const { data, error, isLoading, mutate } = useSWR<OutlookPerfRequestDetail>(url, fetcher, {
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}
